import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Building2, ChevronRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { setStoredClientId } from "@/lib/clientSelection";
import {
  migrateLegacyClientId,
  storageKeyForScope,
  useClientModuleScope,
} from "@/lib/clientModuleScope";
import { ClientWorkPickerList } from "@/components/ClientWorkPickerDialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { VISOR_MODULE_CARD_TITLE } from "@/modules/workModuleCore";

const SCOPE_LABELS = {
  visor_sat: VISOR_MODULE_CARD_TITLE,
  asistente_laboral: "Asistente Laboral / REPSE",
  tramites: "Trámites Fiscales",
};

/** Misma lectura que usePersistedClientId (clave por ámbito, sin fallback cruzado). */
function readScopedClientId(scope) {
  try {
    migrateLegacyClientId(scope);
    return localStorage.getItem(storageKeyForScope(scope)) || "";
  } catch {
    return "";
  }
}

/**
 * Selector de cliente reutilizable por módulo operativo (contexto local al módulo).
 * variant="bar" — barra compacta superior; variant="panel" — acordeón expandido en pantalla.
 *
 * Sin cliente válido: se abre el buscador con la lista visible. Al cambiar un cliente ya
 * elegido, requireSearch evita volcar el catálogo hasta teclear nombre o RFC.
 */
export default function ModuleClientSelector({
  variant = "panel",
  defaultExpanded = false,
  className = "",
}) {
  const moduleScope = useClientModuleScope();
  const { pathname } = useLocation();
  const [clients, setClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [clientId, setClientId] = useState(() => readScopedClientId(moduleScope));
  const [expanded, setExpanded] = useState(() => defaultExpanded || !readScopedClientId(moduleScope));

  const loadClients = useCallback(() => {
    setLoadError("");
    api
      .get("/clients")
      .then(({ data }) => {
        setClients(Array.isArray(data) ? data : []);
        setClientsLoaded(true);
      })
      .catch((err) => {
        setClients([]);
        setClientsLoaded(true);
        const detail = err?.response?.data?.detail;
        const message =
          (typeof detail === "object" && detail?.message)
          || (typeof detail === "string" ? detail : "")
          || "No se pudieron cargar los clientes. Compruebe la sesión e inténtelo de nuevo.";
        setLoadError(message);
      });
  }, []);

  useEffect(() => {
    setClientsLoaded(false);
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    setClientId(readScopedClientId(moduleScope));
  }, [moduleScope, pathname]);

  useEffect(() => {
    const sync = () => setClientId(readScopedClientId(moduleScope));
    const onChanged = (e) => {
      if (e?.detail?.scope && e.detail.scope !== moduleScope) return;
      setClientId(e?.detail?.clientId ?? readScopedClientId(moduleScope));
    };
    window.addEventListener("storage", sync);
    window.addEventListener("visor:client-changed", onChanged);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("visor:client-changed", onChanged);
    };
  }, [moduleScope]);

  const selected = useMemo(
    () => clients.find((c) => c.id === clientId) || null,
    [clients, clientId],
  );

  // Id guardado inválido (borrado / otro espacio) → limpiar y forzar selector.
  useEffect(() => {
    if (!clientsLoaded || !clients.length) return;
    if (clientId && !clients.some((c) => c.id === clientId)) {
      setClientId("");
      setStoredClientId("", moduleScope);
    }
  }, [clientsLoaded, clients, clientId, moduleScope]);

  // Sin cliente válido: abrir siempre el selector (también al cambiar de ruta).
  useEffect(() => {
    if (!readScopedClientId(moduleScope)) {
      setExpanded(true);
      return;
    }
    if (!clientsLoaded) return;
    if (!selected && clients.length > 0) {
      setExpanded(true);
    }
  }, [clientsLoaded, selected, clients.length, pathname, moduleScope]);

  const handleSelect = (id) => {
    if (id === clientId) {
      if (variant === "bar") setExpanded(false);
      return;
    }
    const request = new CustomEvent("visor:client-before-change", {
      detail: { clientId: id, previousClientId: clientId, scope: moduleScope },
      cancelable: true,
    });
    window.dispatchEvent(request);
    if (request.defaultPrevented) {
      if (variant === "bar") setExpanded(false);
      return;
    }
    setClientId(id);
    setStoredClientId(id, moduleScope);
    if (variant === "bar") setExpanded(false);
  };

  const moduleLabel = SCOPE_LABELS[moduleScope] || "Módulo";
  const label = selected?.razon_social || selected?.nombre_comercial || selected?.rfc || "";
  const rfc = (selected?.rfc || "").trim();
  const waitingForClientList = Boolean(clientId) && !clientsLoaded;
  const needsClientPick = clientsLoaded && !selected;

  if (variant === "bar") {
    return (
      <div
        className={`shrink-0 border-b border-[#0F2D5C]/12 bg-[#0F2D5C]/[0.04] px-4 py-2 sm:px-6 ${className}`}
        data-testid="module-client-selector-bar"
        data-needs-client={needsClientPick ? "true" : "false"}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 max-w-[1600px]">
          <div className="flex min-w-0 items-center gap-2 text-sm">
            <Building2 className="h-4 w-4 shrink-0 text-[#0F2D5C]/70" aria-hidden />
            <span className="text-[11px] uppercase tracking-wider font-semibold text-[#0F2D5C]/60">
              Cliente · {moduleLabel}
            </span>
            {waitingForClientList ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-[#0F2D5C]/70">
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                Cargando cliente…
              </span>
            ) : selected ? (
              <>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#0F2D5C]/30" aria-hidden />
                <span className="font-semibold text-[#0F2D5C] truncate max-w-[min(100%,28rem)]" title={label}>
                  {label || "Sin razón social"}
                </span>
                {rfc ? (
                  <span className="font-mono text-xs text-[#0F2D5C]/75 shrink-0">{rfc}</span>
                ) : null}
                <span className="hidden sm:inline text-[9px] font-bold uppercase tracking-wide text-[#3DA873]">
                  Activo
                </span>
              </>
            ) : loadError ? (
              <span className="text-amber-900/90 text-xs font-medium" data-testid="module-client-selector-error">
                {loadError}
              </span>
            ) : (
              <span className="text-amber-900/90 text-xs font-medium">
                Sin cliente — elija uno para operar
              </span>
            )}
          </div>
          {clients.length > 0 ? (
            <button
              type="button"
              onClick={() => {
                // Sin cliente no se permite colapsar: el listado debe quedar visible.
                if (needsClientPick) {
                  setExpanded(true);
                  return;
                }
                setExpanded((v) => !v);
              }}
              className="text-xs font-medium text-[#0F2D5C]/80 hover:text-[#0F2D5C] underline-offset-2 hover:underline shrink-0"
              data-testid="module-client-selector-toggle"
              aria-expanded={expanded}
            >
              {selected ? "Cambiar cliente" : "Elegir cliente"}
            </button>
          ) : clientsLoaded ? (
            <div className="flex items-center gap-2 shrink-0">
              {loadError ? (
                <button
                  type="button"
                  onClick={loadClients}
                  className="text-xs font-medium text-amber-900 hover:underline"
                  data-testid="module-client-selector-retry"
                >
                  Reintentar
                </button>
              ) : null}
              <Link to="/clientes" className="text-xs font-medium text-[#0F2D5C]/80 hover:underline">
                Ir a Clientes
              </Link>
            </div>
          ) : (
            <span className="text-xs text-[#0F2D5C]/50 shrink-0">Cargando…</span>
          )}
        </div>
        {expanded && clients.length > 0 ? (
          <div
            className="max-w-[1600px] mt-3 pb-1"
            data-testid="module-client-selector-picker"
          >
            {!selected ? (
              <p className="text-[11px] text-amber-900/80 m-0 mb-2">
                Debe seleccionar un cliente para operar en este módulo (p. ej. Confronta, Visor, Carga).
                Escriba el nombre o RFC para filtrar, o elija de la lista.
              </p>
            ) : null}
            <ClientWorkPickerList
              clients={clients}
              clientId={clientId}
              onSelect={handleSelect}
              requireSearch={Boolean(selected)}
              autoFocusSearch={!selected}
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <section
      className={`rounded-sm border border-slate-200 bg-slate-50/80 ${className}`}
      data-testid="module-client-selector-panel"
    >
      <Accordion
        type="single"
        collapsible
        value={expanded || needsClientPick ? "cliente-modulo" : ""}
        onValueChange={(v) => setExpanded(v === "cliente-modulo")}
      >
        <AccordionItem value="cliente-modulo" className="border-0">
          <AccordionTrigger className="px-4 py-3 hover:no-underline" data-testid="module-client-selector-trigger">
            <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Cliente de trabajo · {moduleLabel}
              </span>
              {selected ? (
                <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-sm font-semibold text-[#0F2D5C] truncate max-w-full">
                    {label || "Sin razón social"}
                  </span>
                  <span className="text-xs font-mono text-slate-600 shrink-0">{rfc}</span>
                  <span className="text-[9px] font-bold uppercase tracking-wide text-[#3DA873]">Activo</span>
                </span>
              ) : (
                <span className="text-xs text-slate-500 font-normal">
                  Seleccione el RFC con el que operará en este módulo
                </span>
              )}
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 pt-0 border-t border-slate-200">
            <p className="text-xs text-slate-600 mt-3 mb-3">
              El cliente elegido aquí aplica a <strong>{moduleLabel}</strong> y al resto de módulos operativos
              mientras su sesión esté activa. Escriba el nombre o RFC para filtrar, o elija de la lista.
            </p>
            {loadError ? (
              <p className="text-xs text-amber-900 mb-3" data-testid="module-client-selector-panel-error">
                {loadError}
              </p>
            ) : null}
            <ClientWorkPickerList
              clients={clients}
              clientId={clientId}
              onSelect={handleSelect}
              requireSearch={Boolean(selected)}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 pt-3">
              <p className="text-[11px] text-slate-500 m-0">
                {clientId ? "Selección guardada para este módulo." : "Elija un cliente de la lista."}
              </p>
              <Link to="/clientes" className="text-xs text-[#0F2D5C] font-medium underline shrink-0">
                Gestionar clientes
              </Link>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}
